import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './ImageCarousel.module.scss';

type ImageItem = 
  | { type: 'new'; file: File }
  | { type: 'existing'; id: number; url: string };

interface ImageCarouselProps {
  images: ImageItem[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onAddImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenStarterKit?: () => void;
  canOpenStarterKit?: boolean;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  currentIndex,
  onIndexChange,
  onDeleteImage,
  onAddImage,
  onOpenStarterKit,
  canOpenStarterKit = false,
}) => {
  const objectUrlsRef = useRef<Map<string, string>>(new Map());
  
  const getFileKey = useCallback((file: File): string => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }, []);
  
  const validIndex = useMemo(() => {
    if (images.length === 0) return 0;
    return Math.max(0, Math.min(currentIndex, images.length - 1));
  }, [images.length, currentIndex]);

  useEffect(() => {
    if (images.length > 0 && validIndex !== currentIndex) {
      onIndexChange(validIndex);
    }
  }, [images.length, validIndex, currentIndex, onIndexChange]);

  useEffect(() => {
    const urls = objectUrlsRef.current;
    const currentFiles = images
      .filter(img => img.type === 'new')
      .map(img => ({ file: img.file, key: getFileKey(img.file) }));
    
    const currentFileKeys = new Set(currentFiles.map(item => item.key));
    
    const keysToRemove: string[] = [];
    urls.forEach((url, key) => {
      if (!currentFileKeys.has(key)) {
        URL.revokeObjectURL(url);
        keysToRemove.push(key);
      }
    });
    
    keysToRemove.forEach(key => urls.delete(key));
  }, [images, getFileKey]);
  
  const currentImage = images[validIndex] || null;

  const imageSrc = useMemo(() => {
    if (!currentImage) return null;
    
    if (currentImage.type === 'new') {
      const urls = objectUrlsRef.current;
      const fileKey = getFileKey(currentImage.file);
      let url = urls.get(fileKey);
      if (!url) {
        url = URL.createObjectURL(currentImage.file);
        urls.set(fileKey, url);
      }
      return url;
    } else {
      return currentImage.url;
    }
  }, [currentImage, getFileKey]);

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length === 0) return;
    onIndexChange(validIndex > 0 ? validIndex - 1 : images.length - 1);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length === 0) return;
    onIndexChange(validIndex < images.length - 1 ? validIndex + 1 : 0);
  };

  const handleDotClick = (index: number) => {
    if (index >= 0 && index < images.length) {
      onIndexChange(index);
    }
  };

  if (!images || images.length === 0) {
    return null;
  }

  if (!currentImage) {
    return null;
  }

  if (!imageSrc) {
    return (
      <div className={styles.carousel}>
        <div className={styles.carousel_container}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando imagen...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.carousel}>
      <div className={styles.carousel_container}>
        <img
          src={imageSrc}
          alt={`Imagen ${validIndex + 1}`}
          className={styles.carousel_image}
        />
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeleteImage(validIndex);
          }}
          className={styles.deleteButton}
          aria-label="Eliminar imagen"
        >
          ×
        </button>

        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className={styles.navButton}
              aria-label="Imagen anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={handleNext}
              className={`${styles.navButton} ${styles.navButton_right}`}
              aria-label="Siguiente imagen"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        <div className={styles.addButtonContainer}>
        <div className={styles.addButtonRow}>
          <label htmlFor="add-images-carousel" className={styles.addButton}>
            <span>Seleccionar archivos</span>
            <input
              type="file"
              id="add-images-carousel"
              onChange={onAddImage}
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
              style={{ display: 'none' }}
              accept="image/*"
              multiple
            />
          </label>
          {onOpenStarterKit && canOpenStarterKit && (
            <button
              type="button"
              className={styles.catalogButton}
              onClick={(e) => {
                e.stopPropagation()
                onOpenStarterKit()
              }}
            >
              Catálogo
            </button>
          )}
        </div>
        </div>
      </div>

      {images.length > 1 && (
        <div className={styles.dots}>
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`${styles.dot} ${
                index === validIndex ? styles.dot_active : ''
              }`}
              aria-label={`Ir a imagen ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
